import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import ConfirmModal from '@/components/seop/ConfirmModal';
import EditorDeLotes from '@/components/seop/EditorDeLotes';
import EditorDeRecursos from '@/components/seop/EditorDeRecursos';
import MesesDoPlano from '@/components/seop/MesesDoPlano';
import EmptyState from '@/components/seop/EmptyState';
import { useToast } from '@/contexts/ToastContext';
import { seopService } from '@/services/seop.service';
import type {
  SeopLotParam,
  SeopPhysicalResource,
  SeopProductDetail,
  SeopResource,
  SeopRouteStep,
} from '@/types/seop';
import { primeiraMensagem } from '@/utils/seopErrors';
import { fmtMoneyBRL, parseMoneyBRL } from '@/utils/seopFormat';
import { opcoesDeRecurso } from '@/utils/seopRecursos';

/** Teto do original: `Alternativo 1..5`. O motor descarta a sexta (borda testada no B02). */
const MAX_ALTERNATIVAS = 5;

/**
 * As etapas do processo, na ordem em que ele acontece.
 *
 * Nome de etapa NÃO é rótulo livre: as regras de alocação casam por ele
 * (`default_rules.py`), e `PRODUÇÃO DE FRASCOS` e `ENVASADORA` precisam bater
 * exatamente — com um caractere diferente o produto deixa de gerar carga, sem erro
 * nenhum. As demais só influenciam a ordenação, que o motor faz por trecho do nome
 * (`stage_sequence_index`).
 *
 * A lista existe para o caso de um plano SEM rota nenhuma: um vocabulário tirado só dos
 * dados seria vazio ali, e ninguém conseguiria cadastrar a primeira etapa — que é
 * exatamente o beco que esta tela tinha.
 */
const ETAPAS_CANONICAS = [
  'PRODUÇÃO DE FRASCOS',
  'FÍSICO-QUÍMICO',
  'MICROBIOLÓGICO',
  'ENVASADORA',
  'AUTOCLAVE',
  'REVISÃO / ROTULAGEM',
] as const;

const INPUT = 'w-full rounded-lg border border-theme-border bg-surface-secondary px-2 py-1 text-sm';
const CELL = 'py-2 pr-3 text-sm text-content-primary';

type Bloco = 'meses' | 'produtos' | 'roteiro' | 'lotes' | 'recursos';

const Secao: FC<{ id: Bloco; titulo: string; aberto: Bloco; onToggle: (b: Bloco) => void; children: ReactNode }> = ({
  id,
  titulo,
  aberto,
  onToggle,
  children,
}) => (
  <div className="rounded-xl border border-theme-border bg-surface-card">
    <button
      type="button"
      data-testid={`seop-bloco-${id}`}
      onClick={() => onToggle(id)}
      className="flex w-full items-center justify-between px-5 py-4 text-left"
    >
      <span className="font-semibold text-content-primary">{titulo}</span>
      <span className="material-symbols-outlined text-content-muted">
        {aberto === id ? 'expand_less' : 'expand_more'}
      </span>
    </button>
    {aberto === id && <div className="border-t border-theme-border px-5 py-4">{children}</div>}
  </div>
);

interface Impacto {
  rotas: number;
  lotes: number;
}

type AlvoExclusao =
  | { tipo: 'produto'; alvo: SeopProductDetail }
  | { tipo: 'recurso'; alvo: SeopResource }
  | null;

const ModalExclusao: FC<{
  alvo: AlvoExclusao;
  impacto: Impacto | null;
  isBusy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ alvo, impacto, isBusy, onConfirm, onCancel }) => {
  const ehProduto = alvo?.tipo === 'produto';
  const nome = alvo?.tipo === 'produto' ? alvo.alvo.code : alvo?.alvo.name;
  return (
    <ConfirmModal
      open={alvo !== null}
      title={ehProduto ? `Excluir o produto ${nome}?` : `Excluir o recurso ${nome ?? ''}?`}
      confirmLabel="Excluir"
      isBusy={isBusy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <p data-testid="seop-impacto">
        {ehProduto ? (
          <>
            Este produto tem <strong>{impacto?.rotas ?? 0} etapa(s)</strong> de roteiro e{' '}
            <strong>{impacto?.lotes ?? 0} parâmetro(s) de lote</strong>, além da demanda de todos os
            meses.
          </>
        ) : (
          <>
            Este recurso está no roteiro de <strong>{impacto?.rotas ?? 0} produto(s)</strong> e em{' '}
            <strong>{impacto?.lotes ?? 0} parâmetro(s) de lote</strong>.
          </>
        )}
      </p>
      <p>Esta ação não tem desfazer — salve um cenário antes se quiser poder voltar.</p>
    </ConfirmModal>
  );
};

interface EditorDeRoteiroProps {
  produto: SeopProductDetail | null;
  produtos: SeopProductDetail[];
  nomes: string[];
  vocabularioEtapas: string[];
  etapaNova: string;
  onTrocarProduto: (id: string) => void;
  onEtapaNova: (etapa: string) => void;
  onSalvar: (indice: number, dados: Partial<SeopRouteStep>) => void;
  onAdicionar: () => void;
  onRemover: (indice: number) => void;
}

/**
 * O roteiro de um produto: as etapas, o recurso de cada uma, os alternativos, e o
 * acréscimo/remoção de etapa.
 *
 * Componente à parte porque a tela de Cadastros já carrega quatro blocos e este é o
 * único com tabela editável — deixá-lo embutido estourava o teto de complexidade do
 * ESLint, que é o sinal de que o bloco tinha vida própria.
 */
const EditorDeRoteiro: FC<EditorDeRoteiroProps> = ({
  produto,
  produtos,
  nomes,
  vocabularioEtapas,
  etapaNova,
  onTrocarProduto,
  onEtapaNova,
  onSalvar,
  onAdicionar,
  onRemover,
}) => (
  <>
    <label className="mb-3 block text-xs text-content-muted">
      Produto
      <select
        aria-label="Produto do roteiro"
        value={produto?.id ?? ''}
        onChange={(e) => onTrocarProduto(e.target.value)}
        className={`${INPUT} mt-1 max-w-md`}
      >
        {produtos.map((p) => (
          <option key={p.id} value={p.id}>
            {p.code} — {p.description}
          </option>
        ))}
      </select>
    </label>
    {!produto ? (
      <EmptyState icon="alt_route" title="Nenhum produto selecionado" hint="Escolha um produto acima." />
    ) : (produto.route ?? []).length === 0 ? (
      <EmptyState
        icon="alt_route"
        title="Produto sem roteiro"
        hint="Sem etapas o produto não gera carga em recurso nenhum — cadastre ao menos uma etapa abaixo."
      />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
              <th className="pb-2 pr-3 text-left">Ordem</th>
              <th className="pb-2 pr-3 text-left">Etapa</th>
              <th className="pb-2 pr-3 text-left">Principal</th>
              {Array.from({ length: MAX_ALTERNATIVAS }, (_, i) => (
                <th key={i} className="pb-2 pr-3 text-left">
                  Alt. {i + 1}
                </th>
              ))}
              <th className="pb-2 text-left">
                <span className="sr-only">Remover etapa</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(produto.route ?? []).map((rt, i) => (
              <tr key={rt.id} className="border-b border-theme-border/50">
                <td className={CELL}>{rt.index + 1}</td>
                <td className={CELL}>{rt.stage}</td>
                <td className={CELL}>
                  <select
                    aria-label={`Recurso principal da etapa ${rt.stage}`}
                    value={rt.resource}
                    onChange={(e) => onSalvar(i, { resource: e.target.value })}
                    className={INPUT}
                  >
                    {opcoesDeRecurso(nomes, rt.resource).map((o) => (
                      <option key={o.valor} value={o.valor}>
                        {o.rotulo}
                      </option>
                    ))}
                  </select>
                </td>
                {Array.from({ length: MAX_ALTERNATIVAS }, (_, k) => (
                  <td key={k} className={CELL}>
                    <select
                      aria-label={`Alternativo ${k + 1} da etapa ${rt.stage}`}
                      value={(rt.alternatives ?? [])[k] ?? ''}
                      onChange={(e) => {
                        const alts = [...(rt.alternatives ?? [])];
                        alts[k] = e.target.value;
                        onSalvar(i, {
                          alternatives: alts.filter(Boolean).slice(0, MAX_ALTERNATIVAS),
                        });
                      }}
                      className={INPUT}
                    >
                      <option value="">—</option>
                      {opcoesDeRecurso(nomes, (rt.alternatives ?? [])[k] ?? '').map((o) => (
                        <option key={o.valor} value={o.valor}>
                          {o.rotulo}
                        </option>
                      ))}
                    </select>
                  </td>
                ))}
                <td className={CELL}>
                  <button
                    type="button"
                    data-testid={`seop-remover-etapa-${i}`}
                    aria-label={`Remover a etapa ${rt.stage}`}
                    onClick={() => onRemover(i)}
                    className="rounded-lg p-1 text-content-muted hover:text-red-400"
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-content-muted">
          O teto é de {MAX_ALTERNATIVAS} alternativos — o motor descarta a sexta opção.
        </p>
      </div>
    )}
    {produto && (
      <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-theme-border pt-4">
        <label className="text-xs text-content-muted">
          Nova etapa
          <select
            aria-label="Etapa a adicionar"
            value={etapaNova}
            onChange={(e) => onEtapaNova(e.target.value)}
            className={`${INPUT} mt-1 min-w-56`}
          >
            {vocabularioEtapas.map((etapa) => (
              <option key={etapa} value={etapa}>
                {etapa}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          data-testid="seop-adicionar-etapa"
          onClick={() => onAdicionar()}
          disabled={nomes.length === 0}
          className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-surface-primary disabled:opacity-50"
        >
          Adicionar etapa
        </button>
        {nomes.length === 0 && (
          <span className="text-xs text-content-muted">Cadastre ao menos um recurso antes.</span>
        )}
      </div>
    )}
  </>
);

const SeopCatalogPage: FC = () => {
  const { showToast } = useToast();
  const [aberto, setAberto] = useState<Bloco>('produtos');
  const [products, setProducts] = useState<SeopProductDetail[]>([]);
  const [resources, setResources] = useState<SeopResource[]>([]);
  const [lotParams, setLotParams] = useState<SeopLotParam[]>([]);
  const [fisicos, setFisicos] = useState<SeopPhysicalResource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [produtoRoteiro, setProdutoRoteiro] = useState('');
  const [etapaNova, setEtapaNova] = useState<string>(ETAPAS_CANONICAS[0]);
  const [excluindo, setExcluindo] = useState<AlvoExclusao>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [ps, rs, lp, ph] = await Promise.all([
        seopService.getProductsDetail(),
        seopService.getResources(),
        seopService.getLotParams(),
        seopService.getPhysicalResources(),
      ]);
      setProducts(ps);
      setResources(rs);
      setLotParams(lp);
      setFisicos(ph);
      if (!produtoRoteiro && ps.length) setProdutoRoteiro(ps[0].id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
    // `produtoRoteiro` fora das deps de propósito: recarregar não deve trocar a seleção
    // do usuário, só preencher a primeira vez.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** O que quebra ao excluir — é isso que decide se a pessoa continua. */
  const impacto = useMemo(() => {
    if (!excluindo) return null;
    if (excluindo.tipo === 'produto') {
      const p = excluindo.alvo;
      return {
        rotas: p.route?.length ?? 0,
        lotes: lotParams.filter((l) => l.product === p.id).length,
      };
    }
    const nome = excluindo.alvo.name;
    return {
      rotas: products.filter((p) =>
        (p.route ?? []).some((rt) => rt.resource === nome || (rt.alternatives ?? []).includes(nome)),
      ).length,
      lotes: lotParams.filter((l) => l.resource === nome).length,
    };
  }, [excluindo, products, lotParams]);

  const salvarProduto = async (p: SeopProductDetail, campo: string, valor: string | number) => {
    try {
      await seopService.updateProduct(p.id, { [campo]: valor });
      await fetchData();
    } catch {
      showToast('Erro ao salvar o produto.', 'error');
    }
  };

  const nomes = resources.map((r) => r.name);

  /**
   * As etapas oferecidas: as do processo mais qualquer outra já em uso no plano.
   *
   * Sair dos DADOS é o que faz a lista acompanhar uma linha nova sem deploy; a união com
   * as canônicas é o que impede a lista vazia num plano ainda sem rota. Ordem: processo
   * primeiro, o resto em seguida, para o operador achar onde espera.
   */
  const vocabularioEtapas = useMemo(() => {
    const emUso = new Set(products.flatMap((p) => (p.route ?? []).map((rt) => rt.stage)).filter(Boolean));
    const canonicas: string[] = [...ETAPAS_CANONICAS];
    const extras = [...emUso].filter((e) => !canonicas.includes(e)).sort();
    return [...canonicas, ...extras];
  }, [products]);

  /**
   * Grava a rota INTEIRA — é assim que a API a aceita, e é o que torna a remoção de etapa
   * exprimível (um PATCH incremental não teria como dizer "esta etapa deixou de existir").
   *
   * A atualização é otimista com reversão no erro. Os `<select>` são controlados por este
   * estado: sem o otimismo a escolha só apareceria depois do refetch, e sem a reversão a
   * tela voltaria a mentir — era assim que o campo não-controlado deixava a opção na tela
   * mesmo quando nada tinha sido gravado.
   */
  const aplicarRota = async (produto: SeopProductDetail, rota: SeopRouteStep[]) => {
    const anterior = produto.route ?? [];
    const trocar = (r: SeopRouteStep[]) => (atual: SeopProductDetail[]) =>
      atual.map((p) => (p.id === produto.id ? { ...p, route: r } : p));
    setProducts(trocar(rota));
    try {
      await seopService.updateProduct(produto.id, { route: rota } as Partial<SeopProductDetail>);
      await fetchData();
    } catch (e) {
      setProducts(trocar(anterior));
      showToast(primeiraMensagem(e) ?? 'Erro ao salvar o roteiro.', 'error');
    }
  };

  const salvarRota = (produto: SeopProductDetail, indice: number, dados: Partial<SeopRouteStep>) =>
    aplicarRota(
      produto,
      (produto.route ?? []).map((rt, i) => (i === indice ? { ...rt, ...dados } : rt)),
    );

  const adicionarEtapa = (produto: SeopProductDetail) =>
    aplicarRota(produto, [
      ...(produto.route ?? []),
      // `id` e `index` vêm do servidor; aqui são só marcadores até o refetch. A etapa entra
      // no fim porque a ordem GRAVADA é cosmética: o motor reordena por processo
      // (`sort_routes_by_process`) antes de calcular.
      { id: `novo-${Date.now()}`, index: (produto.route ?? []).length, stage: etapaNova, resource: nomes[0] ?? '', alternatives: [] },
    ]);

  const removerEtapa = (produto: SeopProductDetail, indice: number) =>
    aplicarRota(
      produto,
      (produto.route ?? []).filter((_, i) => i !== indice),
    );

  const confirmarExclusao = async () => {
    if (!excluindo) return;
    setIsSaving(true);
    try {
      if (excluindo.tipo === 'produto') await seopService.deleteProduct(excluindo.alvo.id);
      else await seopService.deleteResource(excluindo.alvo.id);
      setExcluindo(null);
      showToast('Excluído.', 'success');
      await fetchData();
    } catch {
      showToast('Erro ao excluir.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="p-6 lg:p-10 text-content-muted">Carregando os cadastros…</div>;

  if (error) {
    return (
      <div className="p-6 lg:p-10">
        <div className="rounded-lg border border-theme-danger/40 bg-theme-danger/10 p-4 text-content-primary">
          Não foi possível carregar os cadastros. {error}
        </div>
      </div>
    );
  }

  const produtoSelecionado = products.find((p) => p.id === produtoRoteiro) ?? null;

  return (
    <div className="space-y-4 p-6 lg:p-10">
      <Secao id="meses" titulo="Meses do plano" aberto={aberto} onToggle={setAberto}>
        <MesesDoPlano />
      </Secao>

      <Secao id="produtos" titulo="Produtos" aberto={aberto} onToggle={setAberto}>
        {products.length === 0 ? (
          <EmptyState
            icon="inventory_2"
            title="Nenhum produto cadastrado"
            hint="Cadastre um produto pela tela de Demanda (bloco “Novo plano”) e volte para montar o roteiro."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
                  <th className="pb-2 pr-3 text-left">Código</th>
                  <th className="pb-2 pr-3 text-left">Produto</th>
                  <th className="pb-2 pr-3 text-left">Linha</th>
                  <th className="pb-2 pr-3 text-left">Volume</th>
                  <th className="pb-2 pr-3 text-right">PMV</th>
                  <th className="pb-2 pr-3 text-right">CPV</th>
                  <th className="pb-2 pr-3 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-theme-border/50 hover:bg-surface-hover/50">
                    <td className={CELL}>{p.code}</td>
                    <td className={CELL}>
                      <input
                        aria-label={`Descrição ${p.code}`}
                        defaultValue={p.description}
                        onBlur={(e) => salvarProduto(p, 'description', e.target.value)}
                        className={INPUT}
                      />
                    </td>
                    <td className={CELL}>
                      <select
                        aria-label={`Linha ${p.code}`}
                        defaultValue={p.line || ''}
                        onChange={(e) => salvarProduto(p, 'line', e.target.value)}
                        className={INPUT}
                      >
                        <option value="">—</option>
                        <option value="SPGV">SPGV</option>
                        <option value="SPPV">SPPV</option>
                      </select>
                    </td>
                    <td className={CELL}>
                      <input
                        aria-label={`Volume do produto ${p.code}`}
                        defaultValue={p.volume}
                        onBlur={(e) => salvarProduto(p, 'volume', e.target.value)}
                        className={INPUT}
                      />
                    </td>
                    <td className={`${CELL} text-right`}>
                      <input
                        aria-label={`PMV do cadastro ${p.code}`}
                        defaultValue={fmtMoneyBRL(p.unit_value ?? 0)}
                        onBlur={(e) => salvarProduto(p, 'unit_value', parseMoneyBRL(e.target.value))}
                        className={`${INPUT} text-right`}
                      />
                    </td>
                    <td className={`${CELL} text-right`}>
                      <input
                        aria-label={`CPV do cadastro ${p.code}`}
                        defaultValue={fmtMoneyBRL(p.cpv_unit_value ?? 0)}
                        onBlur={(e) => salvarProduto(p, 'cpv_unit_value', parseMoneyBRL(e.target.value))}
                        className={`${INPUT} text-right`}
                      />
                    </td>
                    <td className={CELL}>
                      <button
                        type="button"
                        data-testid={`seop-excluir-produto-${p.code}`}
                        onClick={() => setExcluindo({ tipo: 'produto', alvo: p })}
                        className="rounded-lg border border-theme-border px-2 py-1 text-xs text-theme-danger hover:bg-theme-danger/10"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Secao>

      <Secao id="roteiro" titulo="Roteiro por produto" aberto={aberto} onToggle={setAberto}>
        <EditorDeRoteiro
          produto={produtoSelecionado}
          produtos={products}
          nomes={nomes}
          vocabularioEtapas={vocabularioEtapas}
          etapaNova={etapaNova}
          onTrocarProduto={setProdutoRoteiro}
          onEtapaNova={setEtapaNova}
          onSalvar={(i, dados) => produtoSelecionado && salvarRota(produtoSelecionado, i, dados)}
          onAdicionar={() => produtoSelecionado && adicionarEtapa(produtoSelecionado)}
          onRemover={(i) => produtoSelecionado && removerEtapa(produtoSelecionado, i)}
        />
      </Secao>

      <Secao id="lotes" titulo="Parâmetros de lote" aberto={aberto} onToggle={setAberto}>
        <EditorDeLotes produtos={products} nomes={nomes} lotes={lotParams} onRecarregar={fetchData} />
      </Secao>

      <Secao id="recursos" titulo="Recursos" aberto={aberto} onToggle={setAberto}>
        <EditorDeRecursos
          recursos={resources}
          fisicos={fisicos}
          onRecarregar={fetchData}
          onExcluirRecurso={(r) => setExcluindo({ tipo: 'recurso', alvo: r })}
        />
      </Secao>

      <ModalExclusao
        alvo={excluindo}
        impacto={impacto}
        isBusy={isSaving}
        onConfirm={confirmarExclusao}
        onCancel={() => setExcluindo(null)}
      />
    </div>
  );
};

export default SeopCatalogPage;
